import { usePageTitle } from "@/hooks/usePageTitle";
import { useEffect, useState } from "react";
import { useParams, useSearchParams, Navigate, Link } from "react-router-dom";
import { ArrowLeft, MapPin } from "lucide-react";
import { toast } from "sonner";
import { NavBar } from "@/components/NavBar";
import { Skeleton } from "@/components/ui/skeleton";
import { OrderSummary } from "@/components/checkout/OrderSummary";
import { PaymentChoice } from "@/components/checkout/PaymentChoice";
import { UpiPayment } from "@/components/checkout/UpiPayment";
import { ProofUpload } from "@/components/checkout/ProofUpload";
import { OrderPlaced } from "@/components/checkout/OrderPlaced";
import { AddressStep, addressLine } from "@/components/checkout/AddressStep";
import { api } from "@/lib/lamka";
import { startOrder, getContact, sendOrderNote, uploadImage, submitUpiProof, submitCod } from "@/lib/order";
import { useAuth } from "@/context/AuthContext";

const errMsg = (e) => e?.response?.data?.detail || "Something went wrong. Please try again.";

export default function Checkout() {
  usePageTitle("Checkout");
  const { id } = useParams();
  const [params] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const [item, setItem] = useState(null);
  const [qty, setQty] = useState(Math.max(1, Number(params.get("qty")) || 1));
  const [method, setMethod] = useState("");
  const [step, setStep] = useState("address");
  const [address, setAddress] = useState(null);
  const [conv, setConv] = useState(null);
  const [vpa, setVpa] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploaded, setUploaded] = useState(null);
  const size = params.get("size");
  const colour = params.get("colour");

  useEffect(() => {
    if (user)
      api.get(`/listings/${id}`)
        .then((r) => {
          setItem(r.data);
          setQty((q) => Math.min(q, Math.max(1, r.data.stock || 1)));
        })
        .catch(() => toast.error("Couldn't load this item."));
  }, [id, user]);

  if (!authLoading && !user) return <Navigate to="/login" replace />;
  const editable = step === "address" || step === "choose";
  if (item && editable && (item.stock === 0 || item.seller_id === user?.user_id)) return <Navigate to={`/product/${id}`} replace />;

  const amount = item ? item.price * qty : 0;

  const proceed = async () => {
    setBusy(true);
    try {
      let c = conv;
      if (!c) {
        c = await startOrder(item.id, method === "cod" ? "cash" : "upi", qty);
        setConv(c);
        await sendOrderNote(c.id, { size, colour, address: addressLine(address) });
      }
      if (method === "cod") {
        await submitCod(item.id, c.id, qty);
        setStep("done");
        toast.success("Order placed");
      } else {
        const contact = await getContact(item.id);
        if (!contact.upi) throw { response: { data: { detail: "This seller's UPI ID isn't available. Please choose Cash on Delivery." } } };
        setVpa(contact.upi);
        setStep("upi");
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const submitProof = async (file) => {
    setBusy(true);
    try {
      const path = uploaded?.file === file ? uploaded.path : await uploadImage(file);
      setUploaded({ file, path });
      await submitUpiProof(item.id, conv.id, path, qty);
      setStep("done");
      toast.success("Payment proof submitted");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="bg-[#FBF9F5] min-h-screen">
      <NavBar />
      <main className="px-4 sm:px-8 lg:px-12 pt-32 lg:pt-36 pb-24">
        <Link to={`/product/${id}`} data-testid="checkout-back-link" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] hover:text-[#0F0F0F] mb-8">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to item
        </Link>
        <h1 className="font-display text-4xl sm:text-5xl text-[#0F0F0F] mb-12">Checkout</h1>
        {!item ? (
          <Skeleton className="h-80 max-w-3xl rounded-none bg-[#F5EFE6]" />
        ) : (
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-start">
            <OrderSummary item={item} qty={qty} setQty={setQty} size={size} colour={colour} locked={!editable} />
            <div className="min-w-0">
              {step === "address" && <AddressStep onConfirm={(a) => { setAddress(a); setStep("choose"); }} />}
              {step !== "address" && address && (
                <div data-testid="checkout-address-summary" className="flex items-start gap-3 mb-8 pb-6 border-b border-[#C5A059]/25">
                  <MapPin className="h-4 w-4 text-[#C5A059] shrink-0 mt-0.5" strokeWidth={1.5} />
                  <p className="text-sm text-[#3f3f46] flex-1 min-w-0 break-words">{addressLine(address)}</p>
                  {step === "choose" && <button data-testid="checkout-address-change" onClick={() => setStep("address")} className="font-mono text-[10px] uppercase tracking-[0.22em] underline underline-offset-4 shrink-0">Change</button>}
                </div>
              )}
              {step === "choose" && <PaymentChoice method={method} setMethod={setMethod} upiAvailable={item.has_upi !== false} onContinue={proceed} busy={busy} />}
              {step === "upi" && (
                <div className="space-y-10">
                  <UpiPayment vpa={vpa} sellerName={item.seller_name} amount={amount} note={`Lamka: ${item.title}`.slice(0, 50)} />
                  <ProofUpload onSubmit={submitProof} busy={busy} />
                </div>
              )}
              {step === "done" && <OrderPlaced method={method} convId={conv?.id} />}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
